<template>
  <div class="school-search">
    <div v-if="!showAddressFields">
      <v-select v-model="schoolSearch"
                :filterable="false"
                :options="options"
                :placeholder="placeholder"
                :class="{ invalid: showValidation && ( !nonSearchSelected && !school.orgId ) }"
                autocomplete="off"
                :disabled="nonSearchSelected"
                @input="selectAddress"
                @search="onSearch">
        <template slot="no-options">
          {{ helpText }}
        </template>
      </v-select>

      <!--
        <span
          v-show="errors.has('schoolSearch')"
          class="validation-error">
          {{ errors.first('schoolSearch') }}
        </span>
      -->
    </div>

    <div v-if="showAddressFields"
         class="school-fields">
      <div class="form-group">
        <input v-model="school.name"
               placeholder="School name"
               class="form-control"
               :class="{ invalid: showValidation && !school.name }"
               name="orgName"
               aria-label="Enter the name of your school"
               type="text"
               autocomplete="address-name">

        <!--
        <span
          v-show="errors.has('schoolName')"
          class="validation-error">
          {{ errors.first('schoolName') }}
        </span>
        -->
      </div>

      <div class="form-group">
        <input v-model="school.line1"
               placeholder="Address line 1"
               class="form-control"
               :class="{ invalid: showValidation && !school.line1 }"
               name="addressLine1"
               aria-label="Enter the first line of your school's address"
               type="text"
               autocomplete="address-line1">

        <!--
        <span
          v-show="errors.has('schoolLine1')"
          class="validation-error">
          {{ errors.first('schoolLine1') }}
        </span>
        -->
      </div>

      <div class="form-group">
        <input v-model="school.line2"
               placeholder="Address line 2 (optional)"
               class="form-control"
               name="addressLine2"
               aria-label="Enter the second line of your school's address"
               type="text"
               autocomplete="address-line2">
      </div>

      <div class="form-group">
        <input v-model="school.line3"
               placeholder="Address line 3 (optional)"
               class="form-control"
               name="addressLine3"
               aria-label="Enter the third line of your school's address"
               type="text">
      </div>

      <div class="form-group">
        <input v-model="school.town"
               placeholder="Town"
               class="form-control"
               :class="{ invalid: showValidation && !school.town }"
               name="town"
               aria-label="Enter the town / city of your school's address"
               type="text"
               autocomplete="address-level2">

        <!--
        <span
          v-show="errors.has('town')"
          class="validation-error">
          {{ errors.first('town') }}
        </span>
        -->
      </div>

      <div class="form-group">
        <input v-model="school.region"
               placeholder="Region / County"
               class="form-control"
               :class="{ invalid: showValidation && !school.region }"
               name="region"
               aria-label="Enter the region / county of your school's address"
               type="text"
               autocomplete="address-level1">

        <!--
        <span
          v-show="errors.has('region')"
          class="validation-error">
          {{ errors.first('region') }}
        </span>
        -->
      </div>

      <div class="form-group">
        <input v-model="school.postcode"
               placeholder="Postcode"
               class="form-control"
               :class="{ invalid: showValidation && !school.postcode }"
               name="postcode"
               aria-label="Enter the postcode of your school's address"
               type="text"
               autocomplete="postal-code">

        <!--
        <span
          v-show="errors.has('postcode')"
          class="validation-error">
          {{ errors.first('postcode') }}
        </span>
        -->
      </div>
    </div>

    <a v-if="enableManualEntry && !nonSearchSelected"
       href="javascript:void(0)"
       class="cant-find-school"
       @click="showAddressFields = !showAddressFields">{{ searchToggleText }}</a>

    <div v-if="allowNonSearchRegistration && !showAddressFields"
         class="checkbox">
      <input id="nonSearchOption"
             v-model="nonSearchSelected"
             type="checkbox"
             class="checkbox-field">

      <label for="nonSearchOption"
             class="checkbox-label">
        {{ nonSearchLabel }}
      </label>
    </div>
  </div>
</template>

<script>
import vSelect from "vue-select";
import debounce from "lodash/debounce";
import client from "~/modules/client";

export default {
  components: {
    vSelect
  },
  props: {
    value: {
      type: Object,
      default: () => { }
    },
    placeholder: {
      type: String,
      default: "Organisation"
    },
    helpText: {
      type: String,
      default: "Find your school by entering the name or postcode"
    },
    validateRequired: {
      type: Boolean,
      default: false
    },
    enableValidation: {
      type: Boolean,
      default: true
    },
    showValidation: {
      type: Boolean,
      default: true
    },
    defaultToSearch: {
      type: Boolean,
      default: true
    },
    searchTypeId: {
      type: Number,
      default: 0
    },
    enableManualEntry: {
      type: Boolean,
      default: false
    },
    allowNonSearchRegistration: {
      type: Boolean,
      default: false
    },
    nonSearchOrganisationId: {
      type: String,
      default: null
    },
    nonSearchLabel: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      options: [],
      showAddressFields: false,
      schoolSearch: null,
      school: {
        orgId: "",
        name: "",
        line1: "",
        line2: "",
        line3: "",
        town: "",
        region: "",
        postcode: "",
        organisation: ""
      },
      nonSearchSelected: false
    };
  },
  computed: {
    searchToggleText () {
      let result = "Can't find your school?";

      if ( this.showAddressFields ) {
        result = "Search for my school";
      }

      return result;
    },
    validate () {
      let result = false;
      if ( this.enableValidation ) {
        if ( this.showAddressFields || this.nonSearchSelected ) {
          result = false;
        } else {
          result = { required: this.validateRequired, org_address: true };
        }
      }

      return result;
    }
  },
  watch: {
    nonSearchSelected ( val ) {
      if ( val ) {
        this.school.orgId = this.nonSearchOrganisationId;
      } else {
        this.school.orgId = "";
      }
    },
    school: {
      handler ( value ) {
        this.$emit( "input", value );
      },
      deep: true
    },
    value: {
      handler ( data ) {
        this.parseInitial( data );
      },
      deep: true
    },
    enableManualEntry ( val ) {
      if ( !val ) {
        this.showAddressFields = false;
      }
    }
  },
  mounted () {
  },
  created () {
    this.parseInitial( this.value );

    if ( !this.defaultToSearch ) {
      this.showAddressFields = true;
    }
  },
  methods: {
    onSearch ( search, loading ) {
      loading( true );
      this.search( loading, search, this );
    },
    search: debounce( ( loading, search, vm ) => {
      client
        .orgSearch( vm.searchTypeId, search, 20 )
        .then( ( response ) => {
          // eslint-disable-next-line
            vm.options = response.data.results;
          loading( false );
        } )
        .catch( ( error ) => {
          console.error( error );
          loading( false );
        } );
    }, 350 ),
    getFullAddress ( id ) {
      client.getOrgById( id ).then( ( response ) => {
        const { org } = response.data;

        if ( org ) {
          this.school.orgId = org.Id;
          this.school.name = org.Name;
          this.school.line1 = org.AddressLine1;
          this.school.line2 = org.AddressLine2;
          this.school.line3 = org.AddressLine3;
          this.school.town = org.TownOrCity;
          this.school.region = org.Region;
          this.school.postcode = org.Postcode;
        }
      } );
    },
    selectAddress ( school ) {
      this.schoolSearch = school;

      if ( school !== null ) {
        this.getFullAddress( school.value );
      } else {
        Object.keys( this.school ).forEach( ( x ) => {
          this.school[x] = null;
        } );
      }
    },
    parseInitial ( data ) {
      if ( Object.keys( data ).length > 0 ) {
        Object.entries( data ).forEach( ( x ) => {
          const key = x[0];
          const value = x[1];

          if ( Object.prototype.hasOwnProperty.call( this.school, key ) ) {
            this.$set( this.school, key, value );
          }
        } );

        if (
          this.school.orgId
            && !this.schoolSearch
            && !this.showAddressFields
        ) {
          this.schoolSearch = {
            value: this.school.orgId,
            label:
                this.school.name || this.school.organisation || this.school.line1
          };
        }
      }
    }
  }
};
</script>

<style lang="less" scoped>
  .cant-find-school {
    display: block;
    margin-top: 5px;
    cursor: pointer;
  }

    .cant-find-school + .school-fields {
      margin-top: 10px;
    }
</style>
